import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/authService';
import { getProfile } from '../../services/profileService';

/**
 * Component that checks if a user has completed their profile
 * If not, redirects them to the profile edit page
 * This component should be used as a wrapper for routes that require a completed profile
 */
const RequireProfileCompletion = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkProfileCompletion = async () => {
      try {
        // Get current user
        const { user, error: userError } = await getCurrentUser();
        
        if (userError || !user) {
          console.error('Error getting user:', userError?.message);
          navigate('/login');
          return;
        }
        
        // Check user metadata for profile completion flag
        const metadata = user.user_metadata || {};
        const profileSetupRequired = metadata.profile_setup_required === true;
        
        if (profileSetupRequired) {
          console.log('Profile setup required, redirecting to profile edit page');
          let missingFields = [];
          try {
            const { profile: p } = await getProfile(user.id);
            const userType = user.user_metadata?.user_type;
            missingFields = getMissingFields(p, userType);
          } catch (e) {}
          navigate('/profile/edit', { 
            state: { 
              requireCompletion: true,
              message: 'Please complete your profile to continue using the app',
              missingFields 
            } 
          });
          return;
        }
        
        // Get user profile
        const { profile, error: profileError } = await getProfile(user.id);
        
        if (profileError) {
          console.error('Error getting profile:', profileError.message);
          setLoading(false);
          return;
        }
        
        // Check if profile exists and has required fields
        const isProfileComplete = checkIfProfileComplete(profile, user.user_metadata?.user_type);
        
        if (!isProfileComplete) {
          console.log('Profile incomplete, redirecting to profile edit page');
          const missingFields = getMissingFields(profile, user.user_metadata?.user_type);
          navigate('/profile/edit', { 
            state: { 
              requireCompletion: true,
              message: 'Please complete your profile to continue using the app',
              missingFields 
            } 
          });
          return;
        }
        
        setProfileComplete(true);
        setLoading(false);
      } catch (error) {
        console.error('Error checking profile completion:', error.message);
        setLoading(false);
      }
    };
    
    checkProfileCompletion();
  }, [navigate]);
  
  // Function to check if profile has required fields based on user type
  const checkIfProfileComplete = (profile, userType) => {
    if (!profile) return false;
    
    // Common required fields for all users
    const hasCommonRequiredFields = 
      profile.first_name && 
      profile.last_name;
    
    if (!hasCommonRequiredFields) return false;
    
    // User type specific required fields
    const type = userType || (profile && profile.user_type);
    if (type === 'artist') {
      return Array.isArray(profile.professions) && profile.professions.length > 0;
    } else if (type === 'venue') {
      return !!profile.venue_name;
    }
    
    return false;
  };

  const getMissingFields = (profile, userType) => {
    const missing = [];
    if (!profile || !profile.first_name) missing.push('First name');
    if (!profile || !profile.last_name) missing.push('Last name');
    const type = userType || (profile && profile.user_type);
    if (type === 'artist') {
      if (!profile || !Array.isArray(profile.professions) || profile.professions.length === 0) missing.push('Profession(s)');
    } else if (type === 'venue') {
      if (!profile || !profile.venue_name) missing.push('Venue name');
    }
    return missing;
  };
  
  // Show loading state while checking profile
  if (loading) {
    return <div className="loading-container">Checking profile...</div>;
  }
  
  // If profile is complete, render children
  return profileComplete ? children : null;
};

export default RequireProfileCompletion;
